import React from "react";
import Image from "react-bootstrap/Image";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Lesson } from "../common/api";
import { LessonItem, LessonItemLoggingContext } from "../components/LessonItem";
import { ApiContext } from "../common/context/ApiContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { AccountContext } from "../common/context/AccountContext";
import { useLiveData } from "../common/utils";
import { default as UUID } from "uuid";
import { useHistory } from "react-router-dom";
import { LeftCornerDecoration } from "../components/LessonItemBody";
import {
  AnalyticsContext,
  ViewMyClassEvent,
} from "../common/context/AnalyticsContext";
import { AppFlowActionButton, PageContainer } from "../common/CommonStyles";

type MyClassState = {
  lesson_assignments: [Lesson] | null;
};

export const MyClassPage: React.FC = () => {
  const [state, setState] = React.useState<MyClassState>({
    lesson_assignments: null,
  });
  const { setCurrentTab } = React.useContext(TabsContext);
  const { getMyClassMetadata } = React.useContext(ApiContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);

  const { accountLiveData } = React.useContext(AccountContext);
  const myClassFetchedForProfileStateVersion = React.useRef<number | null>(
    null
  );
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const timeoutId = React.useRef<number>();
  const { t } = useTranslation();
  const history = useHistory();
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    logEvent(new ViewMyClassEvent());
  }, [logEvent]);

  React.useEffect(() => {
    setCurrentTab(TabEnum.MyClass);
    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId.current);
      }
    };
  }, [setCurrentTab]);

  const maybeRefetchMyClass = React.useCallback(async () => {
    const account = accountLiveData.value;
    if (account.hasMaybeProfileState()) {
      if (
        account.profileStateCurrentVersion !==
          myClassFetchedForProfileStateVersion.current &&
        account.profileStateCurrentVersion !==
          fetchJobForProfileStateVersion.current
      ) {
        startLoading();
        const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
        fetchJobForProfileStateVersion.current =
          account.profileStateCurrentVersion;
        const myClassMetadata = await getMyClassMetadata(
          account.profile?.id || null,
          account.profileState?.lessons_in_progress || {}
        );
        if (currentFetchJobUuid.current !== fetchJobUuid) {
          return;
        }
        setState({ lesson_assignments: myClassMetadata.lesson_assignments });
        myClassFetchedForProfileStateVersion.current =
          fetchJobForProfileStateVersion.current;
        stopLoading();
        timeoutId.current = window.setTimeout(() => {
          currentFetchJobUuid.current = UUID.v4();
          myClassFetchedForProfileStateVersion.current = null;
          fetchJobForProfileStateVersion.current = null;
          maybeRefetchMyClass();
        }, 8 * 60 * 60 * 1000);
      }
    }
  }, [accountLiveData, getMyClassMetadata, startLoading, stopLoading]);

  useLiveData(maybeRefetchMyClass, accountLiveData);

  if (state.lesson_assignments === null) {
    return <></>;
  }

  const EmptyState: React.FC = () => {
    return (
      <Row
        style={{
          paddingTop: 50,
          paddingLeft: 50,
          paddingRight: 50,
          margin: "auto",
          width: "fit-content",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex" }}>
            <Image
              style={{ margin: "auto", paddingBottom: 50 }}
              src={`${process.env.PUBLIC_URL}/my_class_empty_state.png`}
              fluid
            />
          </div>
          <div>
            <p>{t("my_class_assign_lessons_to")}</p>
            <ul style={{ listStylePosition: "inside" }}>
              <li>{t("my_class_entire_classes")}</li>
              <li>{t("my_class_students_extra_support")}</li>
              <li>{t("my_class_students_move_ahead")}</li>
            </ul>
            <p>{t("my_class_track_student_progress")}</p>
          </div>
        </div>
      </Row>
    );
  };

  return (
    <PageContainer>
      <Row>
        <h2 className="text-center mb-3">
          {t("my_class_title", {
            teacher_name: accountLiveData.value.rosterName(),
          })}
        </h2>
        {state.lesson_assignments!.length > 0 && (
          <h3 className="mb-3">{t("my_class_recently_assigned")}</h3>
        )}
        {state.lesson_assignments!.length <= 0 && <EmptyState />}
        {state.lesson_assignments?.map((lesson) => (
          <Col key={lesson.id} xs={12} lg={6} className="mb-3">
            <LessonItem
              lesson={lesson}
              unitId={null}
              account={accountLiveData.value}
              leftCornerDecoration={LeftCornerDecoration.STANDARD}
              loggingContext={LessonItemLoggingContext.MyClass}
            />
          </Col>
        ))}
      </Row>
      <Col xs={12} md={{ span: 4, offset: 4 }}>
        <AppFlowActionButton
          disabled={false}
          isEnabled={true}
          onClick={() => history.push("/my-class/assignments")}
        >
          {t("my_class_assign_lessons_button_title")}
        </AppFlowActionButton>
      </Col>
    </PageContainer>
  );
};
